/**
 * Department Form Component
 *
 * Modal form for creating/editing departments.
 * Features polished styling with refined inputs and transitions.
 */

import { useState, useEffect } from 'react';
import { X, Building2, Loader2 } from 'lucide-react';
import { useCreateDepartment, useUpdateDepartment } from '../../hooks/useDepartments';
import type { DepartmentWithCount, CreateDepartment } from '@guild/shared';

interface DepartmentFormProps {
  department: DepartmentWithCount | null;
  onClose: () => void;
}

export function DepartmentForm({ department, onClose }: DepartmentFormProps) {
  const isEdit = !!department;
  const [name, setName] = useState(department?.name ?? '');
  const [description, setDescription] = useState(department?.description ?? '');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when department changes
  useEffect(() => {
    setName(department?.name ?? '');
    setDescription(department?.description ?? '');
    setError(null);
  }, [department]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      if (isEdit && department) {
        await updateMutation.mutateAsync({
          id: department.id,
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
          },
        });
      } else {
        const data: CreateDepartment = {
          name: name.trim(),
          description: description.trim() || undefined,
        };
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-mint/50">
              <Building2 size={20} className="text-guild-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Department' : 'New Department'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEdit ? 'Update department details' : 'Create a new department'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Assembly Line A"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Description Field */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Description
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this department's purpose..."
                rows={3}
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  resize-none
                  transition-all duration-200
                "
                disabled={isPending}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {isEdit ? 'Save Changes' : 'Create Department'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
